import { buildUIState } from '@/store/Selectors/UIStateBuilder';
import {
  ChatLayout,
  ChatLayoutConversation,
  ChatLayoutGap,
  ChatLayoutWidgetActions,
  ChatLayoutWidgets,
} from '@/components/chat/ChatLayout';
import { ChatThread } from '@/components/chat/ChatThread';
import { checkAndDispatchTriggersFromQueryString } from '@/actions/QueryTriggerActionCreators';
import {
  CORPUS_QA,
  DATAVOYAGER,
  MULTI_TOOL,
  PAPER_FINDER,
  TABLE_ANNOTATION,
} from 'configs/NoraVariants';
import { EventType } from '@/models/Interaction';
import { fetchThreadState } from '@/store/thunks/FetchThreads';
import { getChatAPI } from '@/api/ChatApi';
import { getCurrentThread, setCurrentThread } from '@/store/reducers/UIState';
import { getThreadById, LOADING_STATE } from '@/store/reducers/ChatStore';
import { optAppConfig } from '@/utils/config';
import { ThreadId } from '@/models/Thread';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { useDebounce } from '@/hooks/browserHooks';
import { WidgetMenu } from '@/components/widgets/WidgetMenu';
import { WidgetPanel } from '@/components/widgets/WidgetPanel';

import { Fragment, useEffect } from 'react';
import { styled } from '@mui/material';
import { useLocation, useNavigation } from 'react-router-dom';
import classNames from 'classnames';

export const ChatPage = () => {
  const location = useLocation();
  const navigation = useNavigation();
  const pathThreadId = parseThreadIdFromPath(location.pathname);

  const pathThread = useAppSelector((state) =>
    getThreadById(state, pathThreadId),
  );

  const currentThread = useAppSelector(getCurrentThread);
  const uiState = useAppSelector(buildUIState);
  const chatApi = getChatAPI();
  const dispatch = useAppDispatch();
  const noraExperienceVariant = optAppConfig((_) => _.experience.variant);
  const shouldRenderWidgetMenu =
    noraExperienceVariant === MULTI_TOOL ||
    noraExperienceVariant === TABLE_ANNOTATION ||
    noraExperienceVariant === DATAVOYAGER;
  const shouldRenderWidgets =
    noraExperienceVariant === MULTI_TOOL ||
    noraExperienceVariant === TABLE_ANNOTATION ||
    noraExperienceVariant === PAPER_FINDER ||
    noraExperienceVariant === DATAVOYAGER;

  const debouncedTrackUIStateEvent = useDebounce(
    (uiState: any, currentThreadId: ThreadId) => {
      chatApi.trackEvent({
        threadId: currentThreadId,
        type: EventType.UI_STATE,
        data: { ...uiState },
      });
    },
    1000,
  );

  const shouldOpenWidgetPanel = useAppSelector(
    (state) => state.UIState.openWidgetIds.length > 0,
  );

  const loadingState = useAppSelector(
    (state) => state.chatStore.initializeThreadsState,
  );

  useEffect(() => {
    // check for triggers that might do something like opening the side panel, etc
    checkAndDispatchTriggersFromQueryString(location.search, dispatch);
  }, []);

  // fetch thread if it wasn't included in the bulk fetch of most recent 50 threads request
  // users could navigate to older threads directly by id in the url
  useEffect(() => {
    if (loadingState === LOADING_STATE.LOADED && !pathThread && pathThreadId) {
      dispatch(fetchThreadState(pathThreadId));
    }
  }, [loadingState, pathThread, pathThreadId]);

  useEffect(() => {
    if (!currentThread) {
      return;
    }
    debouncedTrackUIStateEvent(uiState, currentThread.id);
  }, [uiState, currentThread?.id]);

  // Change the current thread if the path changes
  useEffect(() => {
    if (
      navigation.state === 'loading' &&
      navigation.location?.pathname === '/chat'
    ) {
      dispatch(setCurrentThread(null));
      return;
    }
    if (!pathThread) {
      return;
    }
    if (currentThread !== pathThread) {
      dispatch(fetchThreadState(pathThread.id));
      dispatch(setCurrentThread(pathThread));
    }
  }, [currentThread, pathThread?.id, navigation.location?.pathname]);

  return (
    <ChatLayout
      className={classNames({
        'chat-layout--widgets-open':
          shouldOpenWidgetPanel && shouldRenderWidgets,
        'chat-layout__home': !currentThread,
        'chat-layout__multi-tool': shouldRenderWidgets,
        'chat-layout__corpus-qa': noraExperienceVariant === CORPUS_QA,
        'chat-layout__table-annotation':
          noraExperienceVariant === TABLE_ANNOTATION,
      })}
    >
      <ChatLayoutGap />
      <ChatLayoutConversation>
        {loadingState === LOADING_STATE.LOADED ? (
          <ChatThread />
        ) : (
          <LoadingContainer>Loading...</LoadingContainer>
        )}
      </ChatLayoutConversation>
      {shouldRenderWidgets && (
        <Fragment>
          <ChatLayoutWidgets>
            {shouldOpenWidgetPanel && <WidgetPanel />}
          </ChatLayoutWidgets>
          {shouldRenderWidgetMenu && !!currentThread && (
            <ChatLayoutWidgetActions>
              <WidgetMenu />
            </ChatLayoutWidgetActions>
          )}
        </Fragment>
      )}
    </ChatLayout>
  );
};

const LoadingContainer = styled('div')`
  align-items: center;
  display: flex;
  justify-content: center;
  height: 100%;
  width: 100%;
  color: #ffffff;
`;

export const parseThreadIdFromPath = (path: string): ThreadId | null => {
  const match = path.match(/\/chat\/(.+)/);
  return match ? match[1] : null;
};
